import { FormEvent, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../api/client';
import { OrgLayout } from '../components/OrgLayout';
import { RecordInfoButton } from '../components/RecordInfoButton';
import { Modal } from '../components/Modal';
import { useToast } from '../components/Toast';
import type {
  AcademicYear, ClassSectionDetail, ClassSectionDto, DepartmentDto, Institution, ProgramDto,
} from '../types/models';

const emptyForm = {
  departmentId: '', programId: '', academicYearId: '', classSectionId: '',
  effectiveDate: '', effectiveThruDate: '',
};

// "2026-06-01" -> "01-06-2026"
function fmtDate(iso: string | null) {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return d && m && y ? `${d}-${m}-${y}` : iso;
}

export function ClassSectionDetailsPage() {
  const { orgId = '' } = useParams();
  const toast = useToast();

  const [insts, setInsts] = useState<Institution[]>([]);
  const [depts, setDepts] = useState<DepartmentDto[]>([]);
  const [programs, setPrograms] = useState<ProgramDto[]>([]);
  const [years, setYears] = useState<AcademicYear[]>([]);
  const [details, setDetails] = useState<ClassSectionDetail[] | null>(null);
  const [sectionOptions, setSectionOptions] = useState<ClassSectionDto[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Filter bar (Institution + Academic Year + search).
  const [filter, setFilter] = useState({ institutionId: '', academicYearId: '', search: '' });

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  // Load class sections for the program chosen in the form.
  useEffect(() => {
    if (!form.programId) { setSectionOptions([]); return; }
    api.listClassSections(form.programId).then(setSectionOptions).catch(() => setSectionOptions([]));
  }, [form.programId]);

  async function refresh() {
    try {
      const [i, d, p, y, det] = await Promise.all([
        api.listInstitutions(orgId), api.listDepartments(orgId), api.listPrograms(orgId),
        api.listAcademicYears(orgId), api.listClassSectionDetails(orgId),
      ]);
      setInsts(i); setDepts(d); setPrograms(p); setYears(y); setDetails(det);
    } catch (e: any) {
      setError(e?.message ?? 'Failed to load class section details.');
    }
  }

  // Department → institution code, for the form dropdown label.
  const instCodeOfDept = (departmentId: string) => {
    const dep = depts.find(d => d.id === departmentId);
    return insts.find(i => i.id === dep?.institutionId)?.code ?? '?';
  };

  const programsForForm = useMemo(
    () => programs.filter(p => p.departmentId === form.departmentId),
    [programs, form.departmentId],
  );

  const ready = depts.length > 0 && programs.length > 0 && years.length > 0;

  function setField(key: keyof typeof emptyForm, value: string) {
    setForm(prev => {
      const next = { ...prev, [key]: value };
      if (key === 'departmentId') { next.programId = ''; next.classSectionId = ''; }
      if (key === 'programId') { next.classSectionId = ''; }
      return next;
    });
  }

  function openCreate() {
    setFormError(null); setEditId(null); setForm({ ...emptyForm }); setShowModal(true);
  }

  function openEdit(d: ClassSectionDetail) {
    setFormError(null);
    setEditId(d.id);
    setForm({
      departmentId: d.departmentId, programId: d.programId,
      academicYearId: d.academicYearId, classSectionId: d.classSectionId,
      effectiveDate: d.effectiveDate, effectiveThruDate: d.effectiveThruDate ?? '',
    });
    setShowModal(true);
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setFormError(null);
    if (form.effectiveThruDate && form.effectiveThruDate < form.effectiveDate) {
      setFormError('Effective Thru date cannot be before the Effective date.');
      return;
    }
    setBusy(true);
    const payload = {
      departmentId: form.departmentId, programId: form.programId,
      academicYearId: form.academicYearId, classSectionId: form.classSectionId,
      effectiveDate: form.effectiveDate, effectiveThruDate: form.effectiveThruDate || null,
    };
    try {
      if (editId) await api.updateClassSectionDetail(editId, payload);
      else await api.createClassSectionDetail({ organizationId: orgId, ...payload });
      setShowModal(false);
      await refresh();
      toast.success(editId ? 'Class section detail updated.' : 'Class section detail created.');
    } catch (e: any) {
      setFormError(e?.message ?? 'Failed to save class section detail.');
    } finally {
      setBusy(false);
    }
  }

  async function remove(d: ClassSectionDetail) {
    if (!confirm(`Delete class section detail for “${d.classSectionName}”? This cannot be undone.`)) return;
    setError(null);
    try {
      await api.deleteClassSectionDetail(d.id);
      await refresh();
      toast.success('Class section detail deleted.');
    } catch (e: any) {
      const msg = e?.message ?? 'Failed to delete class section detail.';
      setError(msg);
      toast.error(msg);
    }
  }

  const filtered = useMemo(() => {
    if (!details) return null;
    const q = filter.search.trim().toLowerCase();
    return details.filter(d =>
      (!filter.institutionId || d.institutionId === filter.institutionId)
      && (!filter.academicYearId || d.academicYearId === filter.academicYearId)
      && (!q || d.departmentName.toLowerCase().includes(q)
            || d.classSectionName.toLowerCase().includes(q)
            || d.classSectionCode.toLowerCase().includes(q)));
  }, [details, filter]);

  return (
    <OrgLayout>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <h1 style={{ margin: 0 }}>Class Section Details</h1>
        <button className="primary" onClick={openCreate} disabled={!ready}>+ Add class section detail</button>
      </div>

      {!ready && details && (
        <div className="card" style={{ marginTop: 12 }}>
          <div className="muted">
            You first need a <Link to={`/org/${orgId}/departments`}>department</Link>,{' '}
            <Link to={`/org/${orgId}/programs`}>program</Link>,{' '}
            <Link to={`/org/${orgId}/academic-years`}>academic year</Link> and{' '}
            <Link to={`/org/${orgId}/class-sections`}>class section</Link>.
          </div>
        </div>
      )}

      {error && <div className="error" style={{ margin: '12px 0' }}>{error}</div>}

      <div className="card" style={{ marginTop: 16 }}>
        <div className="row" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label style={{ flex: '1 1 220px' }}>
            <div className="muted">Institution</div>
            <select value={filter.institutionId} onChange={e => setFilter(f => ({ ...f, institutionId: e.target.value }))}>
              <option value="">All</option>
              {insts.map(i => <option key={i.id} value={i.id}>{i.code} — {i.name}</option>)}
            </select>
          </label>
          <label style={{ flex: '1 1 180px' }}>
            <div className="muted">Academic Year</div>
            <select value={filter.academicYearId} onChange={e => setFilter(f => ({ ...f, academicYearId: e.target.value }))}>
              <option value="">All</option>
              {years.map(y => <option key={y.id} value={y.id}>{y.name}</option>)}
            </select>
          </label>
          <label style={{ flex: '1 1 220px' }}>
            <div className="muted">Search</div>
            <input value={filter.search} placeholder="Department / Class Section Name"
                   onChange={e => setFilter(f => ({ ...f, search: e.target.value }))} />
          </label>
          {(filter.institutionId || filter.academicYearId || filter.search) && (
            <button onClick={() => setFilter({ institutionId: '', academicYearId: '', search: '' })}>Clear</button>
          )}
        </div>
      </div>

      <div className="card" style={{ marginTop: 16, padding: 0, overflowX: 'auto' }}>
        <table className="data-table">
          <thead>
            <tr>
              <th style={{ width: 56 }}>S.no</th>
              <th>Department</th>
              <th>Program</th>
              <th>Class Section</th>
              <th>Academic Year</th>
              <th>Effective Date</th>
              <th>Effective Thru Date</th>
              <th style={{ width: 180 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {!details && <tr><td colSpan={8} className="muted">Loading…</td></tr>}
            {details && filtered && filtered.length === 0 && (
              <tr><td colSpan={8} className="muted">
                {details.length === 0 ? 'No class section details yet.' : 'No records match the selected filters.'}
              </td></tr>
            )}
            {filtered?.map((d, idx) => (
              <tr key={d.id}>
                <td>{idx + 1}</td>
                <td style={{ fontWeight: 600 }}>{d.departmentName}</td>
                <td>{d.programCode}</td>
                <td className="slug">{d.classSectionName}</td>
                <td>{d.academicYearName}</td>
                <td>{fmtDate(d.effectiveDate)}</td>
                <td>{fmtDate(d.effectiveThruDate)}</td>
                <td>
                  <button className="link-btn" onClick={() => openEdit(d)}>Edit</button>
                  <button className="link-btn" style={{ color: 'var(--danger)' }} onClick={() => remove(d)}>Delete</button>
                  <RecordInfoButton entity="class-section-details" id={d.id} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <Modal title={editId ? 'Edit class section detail' : 'Create class section detail'}
               onClose={() => setShowModal(false)}>
          <form onSubmit={submit} className="col" style={{ marginTop: 16 }}>
            <div className="row" style={{ gap: 12 }}>
              <label style={{ flex: 1 }}>
                <div className="muted">Department *</div>
                <select value={form.departmentId} required
                        onChange={e => setField('departmentId', e.target.value)}>
                  <option value="" disabled>Select…</option>
                  {depts.map(d => <option key={d.id} value={d.id}>{instCodeOfDept(d.id)} · {d.code} — {d.name}</option>)}
                </select>
              </label>
              <label style={{ flex: 1 }}>
                <div className="muted">Program *</div>
                <select value={form.programId} required disabled={!form.departmentId}
                        onChange={e => setField('programId', e.target.value)}>
                  <option value="" disabled>{form.departmentId ? 'Select…' : 'Pick department first'}</option>
                  {programsForForm.map(p => <option key={p.id} value={p.id}>{p.code} — {p.name}</option>)}
                </select>
              </label>
            </div>
            <div className="row" style={{ gap: 12 }}>
              <label style={{ flex: 1 }}>
                <div className="muted">Academic Year *</div>
                <select value={form.academicYearId} required
                        onChange={e => setField('academicYearId', e.target.value)}>
                  <option value="" disabled>Select…</option>
                  {years.map(y => <option key={y.id} value={y.id}>{y.name}</option>)}
                </select>
              </label>
              <label style={{ flex: 1 }}>
                <div className="muted">Class Section *</div>
                <select value={form.classSectionId} required disabled={!form.programId}
                        onChange={e => setField('classSectionId', e.target.value)}>
                  <option value="" disabled>{form.programId ? 'Select…' : 'Pick program first'}</option>
                  {sectionOptions.map(c => <option key={c.id} value={c.id}>{c.code} — {c.name}</option>)}
                </select>
              </label>
            </div>
            <div className="row" style={{ gap: 12 }}>
              <label style={{ flex: 1 }}>
                <div className="muted">Effective Date *</div>
                <input type="date" value={form.effectiveDate} required
                       onChange={e => setField('effectiveDate', e.target.value)} />
              </label>
              <label style={{ flex: 1 }}>
                <div className="muted">Effective Thru Date</div>
                <input type="date" value={form.effectiveThruDate}
                       onChange={e => setField('effectiveThruDate', e.target.value)} />
              </label>
            </div>
            {formError && <div className="error">{formError}</div>}
            <div className="modal-actions">
              <button type="button" onClick={() => setShowModal(false)}>Cancel</button>
              <button type="submit" className="primary" disabled={busy}>
                {busy ? 'Saving…' : editId ? 'Save changes' : 'Create'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </OrgLayout>
  );
}
